export const UFOV_PROGRESS_STORAGE_KEY = 'ufov.progress.v2';
export const UFOV_LEGACY_PROGRESS_STORAGE_KEYS = ['ufov.UFOVGame.progress.v1'];
export const UFOV_PREMIUM_STORAGE_KEY = 'ufov.premium.v1';
export const UFOV_REVIEWER_ACCESS_EXPIRY_STORAGE_KEY = 'ufov.reviewerAccess.expiry.v1';
export const UFOV_APP_USER_ID_STORAGE_KEY = 'ufov.revenuecat.appUserId.v1';
export const UFOV_ANONYMOUS_RANKING_ID_STORAGE_KEY = 'ufov.ranking.anonymousId.v1';
export const UFOV_ANONYMOUS_RANKING_RESET_TOKEN_STORAGE_KEY = 'ufov.ranking.resetToken.v1';
export const UFOV_ONBOARDING_ANALYTICS_ID_STORAGE_KEY = 'ufov.onboarding.analyticsId.v1';
export const UFOV_ONBOARDING_ANALYTICS_SENT_STORAGE_KEY = 'ufov.onboarding.analyticsSent.v1';
export const UFOV_ONBOARDING_ANALYTICS_OUTBOX_STORAGE_KEY = 'ufov.onboarding.analyticsOutbox.v1';
export const UFOV_ONBOARDING_ANALYTICS_DELIVERED_STORAGE_KEY = 'ufov.onboarding.analyticsDelivered.v1';

const DEFAULT_LIMITS = {
    accessMode: 'hard_paywall',
    freeDailyRunsEnabled: false,
    freeDailyRuns: 5,
    dailyObjectiveRuns: 20,
};

const DEFAULT_REVIEWER_ACCESS_DURATION_SECONDS = 90 * 24 * 60 * 60;

const DEFAULT_BRAND = {
    key: 'ufov',
    name: 'UFOV',
    shortName: 'UFOV',
    fullName: 'UFOV',
    premiumName: 'Advanced',
    domain: 'ufov.app',
    url: 'https://ufov.app',
    supportEmail: 'support@ufov.app',
    logoMarkUrl: '/ufov_logo_mark.png',
    logoMarkPath: 'ufov_logo_mark.png',
    copy: {
        appLabel: 'UFOV app',
        trainingName: 'UFOV training',
        trainingDescriptor: 'speed of processing training',
        onboardingTitle: 'How it works',
        onboardingDescription: 'Watch the center and the outside target after a fast flash.',
        studyDescription: 'SOPT Classic is inspired by the visual speed-of-processing exercises studied in ACTIVE. Findings apply to the studied training protocol, not every game in the app.',
        studyAlt: 'First page of the ACTIVE speed of processing study PDF',
    },
};

const safeWindow = () => (typeof window === 'undefined' ? null : window);
const ONBOARDING_ANALYTICS_OUTBOX_LIMIT = 60;
const ONBOARDING_ANALYTICS_OUTBOX_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const ONBOARDING_ANALYTICS_RETRY_BASE_MS = 2000;
const ONBOARDING_ANALYTICS_RETRY_MAX_MS = 5 * 60 * 1000;
let anonymousAppUserIdMemory = '';
let sharedAnonymousAppUserIdPromise = null;
let onboardingAnalyticsOutboxFlushPromise = null;
let onboardingAnalyticsRetryTimer = null;
let onboardingAnalyticsLifecycleInstalled = false;

const readStorageJson = (key) => {
    const currentWindow = safeWindow();

    if (!currentWindow) {
        return null;
    }

    try {
        const value = currentWindow.localStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    } catch (error) {
        return null;
    }
};

const writeStorageJson = (key, value) => {
    const currentWindow = safeWindow();

    if (!currentWindow) {
        return;
    }

    try {
        currentWindow.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        // Ignore storage failures in private browsing contexts.
    }
};

const writeStorageValue = (key, value) => {
    const currentWindow = safeWindow();

    if (!currentWindow) {
        return;
    }

    try {
        currentWindow.localStorage.setItem(key, value);
    } catch (error) {
        // Ignore storage failures in private browsing contexts.
    }
};

const readStorageValue = (key) => {
    const currentWindow = safeWindow();

    if (!currentWindow) {
        return '';
    }

    try {
        return currentWindow.localStorage.getItem(key) || '';
    } catch (error) {
        return '';
    }
};

const removeStorageValue = (key) => {
    const currentWindow = safeWindow();

    if (!currentWindow) {
        return;
    }

    try {
        currentWindow.localStorage.removeItem(key);
    } catch (error) {
        // Ignore storage failures in private browsing contexts.
    }
};

const readActiveReviewerAccessExpiry = () => {
    const stored = readStorageValue(UFOV_REVIEWER_ACCESS_EXPIRY_STORAGE_KEY);
    const expiresAt = Number(stored);

    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
        return expiresAt;
    }

    if (stored) {
        removeStorageValue(UFOV_REVIEWER_ACCESS_EXPIRY_STORAGE_KEY);
    }

    return null;
};

export const grantReviewerAccess = (durationSeconds = DEFAULT_REVIEWER_ACCESS_DURATION_SECONDS) => {
    const normalizedDuration = Math.max(
        60,
        Math.min(365 * 24 * 60 * 60, Number(durationSeconds) || DEFAULT_REVIEWER_ACCESS_DURATION_SECONDS)
    );
    const expiresAt = Date.now() + (normalizedDuration * 1000);

    writeStorageValue(UFOV_REVIEWER_ACCESS_EXPIRY_STORAGE_KEY, String(expiresAt));
    return expiresAt;
};

export const validateReviewerAccessCode = async (value) => {
    const code = `${value || ''}`.trim().toUpperCase();

    if (!code) {
        return { valid: false, accessDurationSeconds: null };
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);

    try {
        const response = await fetch('/api/reviewer-access/validate', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Cache-Control': 'no-store',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code }),
            signal: controller.signal,
        });

        if (!response.ok) {
            throw new Error(`Reviewer access validation failed with HTTP ${response.status}.`);
        }

        const payload = await response.json();
        const valid = payload?.data?.valid === true;
        const accessDurationSeconds = valid
            ? Number(payload?.data?.access_duration_seconds) || DEFAULT_REVIEWER_ACCESS_DURATION_SECONDS
            : null;

        return { valid, accessDurationSeconds };
    } finally {
        window.clearTimeout(timeout);
    }
};

export const getUFOVConfig = () => {
    const currentWindow = safeWindow();

    return currentWindow?.UFOVConfig || {};
};

export const getUFOVGameConfig = () => getUFOVConfig().game || {};

const isTruthyConfigValue = (value) => (
    value === true
    || ['1', 'true', 'yes', 'on'].includes(`${value || ''}`.trim().toLowerCase())
);

const normalizeIntegerLimit = (value, fallback, min = 0) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? Math.max(min, Math.floor(parsed)) : fallback;
};

export const getUFOVBrandConfig = () => {
    const configuredBrand = getUFOVConfig().brand || {};
    const configuredCopy = configuredBrand.copy || {};

    return {
        ...DEFAULT_BRAND,
        ...configuredBrand,
        key: `${configuredBrand.key || DEFAULT_BRAND.key}`.trim() || DEFAULT_BRAND.key,
        name: `${configuredBrand.name || DEFAULT_BRAND.name}`.trim() || DEFAULT_BRAND.name,
        shortName: `${configuredBrand.shortName || configuredBrand.name || DEFAULT_BRAND.shortName}`.trim() || DEFAULT_BRAND.shortName,
        fullName: `${configuredBrand.fullName || configuredBrand.name || DEFAULT_BRAND.fullName}`.trim() || DEFAULT_BRAND.fullName,
        premiumName: `${configuredBrand.premiumName || DEFAULT_BRAND.premiumName}`.trim() || DEFAULT_BRAND.premiumName,
        logoMarkUrl: `${configuredBrand.logoMarkUrl || DEFAULT_BRAND.logoMarkUrl}`.trim() || DEFAULT_BRAND.logoMarkUrl,
        copy: {
            ...DEFAULT_BRAND.copy,
            ...configuredCopy,
        },
    };
};

export const getUFOVProgressStorageKey = () => {
    const configuredKey = getUFOVGameConfig().progressStorageKey;

    return typeof configuredKey === 'string' && configuredKey.trim()
        ? configuredKey.trim()
        : UFOV_PROGRESS_STORAGE_KEY;
};

export const getUFOVLimits = () => {
    const configuredLimits = getUFOVConfig().limits || {};
    const accessMode = `${configuredLimits.accessMode || DEFAULT_LIMITS.accessMode}`.trim().toLowerCase();
    const freeDailyRunsEnabled = accessMode === 'free_daily_runs'
        || isTruthyConfigValue(configuredLimits.freeDailyRunsEnabled);

    return {
        accessMode: freeDailyRunsEnabled ? 'free_daily_runs' : 'hard_paywall',
        freeDailyRunsEnabled,
        freeDailyRuns: normalizeIntegerLimit(configuredLimits.freeDailyRuns, DEFAULT_LIMITS.freeDailyRuns),
        dailyObjectiveRuns: normalizeIntegerLimit(configuredLimits.dailyObjectiveRuns, DEFAULT_LIMITS.dailyObjectiveRuns, 1),
    };
};

export const isNativeUFOVShell = () => {
    const currentWindow = safeWindow();

    return Boolean(currentWindow?.ReactNativeWebView?.postMessage);
};

export const postToNativeBridge = (type, payload = {}) => {
    const currentWindow = safeWindow();

    if (!currentWindow?.ReactNativeWebView?.postMessage) {
        return false;
    }

    currentWindow.ReactNativeWebView.postMessage(JSON.stringify({
        type,
        ...payload,
    }));

    return true;
};

export const getStoredAppUserId = () => {
    const stored = readStorageValue(UFOV_APP_USER_ID_STORAGE_KEY);

    if (stored) {
        anonymousAppUserIdMemory = stored;
    }

    return stored || anonymousAppUserIdMemory;
};

export const setStoredAppUserId = (appUserId) => {
    if (!appUserId) {
        return;
    }

    anonymousAppUserIdMemory = `${appUserId}`;
    writeStorageValue(UFOV_APP_USER_ID_STORAGE_KEY, appUserId);
};

const createTrackingEventId = () => {
    const currentWindow = safeWindow();

    if (typeof currentWindow?.crypto?.randomUUID === 'function') {
        return currentWindow.crypto.randomUUID();
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

export const trackTikTokEvent = async (event, payload = {}) => {
    const currentWindow = safeWindow();
    const trackingConfig = getUFOVConfig().tracking?.tiktok || {};

    if (!currentWindow || !isTruthyConfigValue(trackingConfig.enabled)) {
        return false;
    }

    const requestPayload = {
        ...payload,
        event,
        event_id: payload.event_id || createTrackingEventId(),
        app_user_id: payload.app_user_id || getStoredAppUserId(),
        platform: payload.platform || (isNativeUFOVShell() ? 'native' : 'web'),
        page_url: currentWindow.location?.href || '',
    };
    const body = JSON.stringify(requestPayload);
    const endpoint = '/api/tiktok/events';

    try {
        if (
            currentWindow.navigator?.sendBeacon
            && typeof currentWindow.Blob === 'function'
        ) {
            const blob = new currentWindow.Blob([body], { type: 'application/json' });
            if (currentWindow.navigator.sendBeacon(endpoint, blob)) {
                return true;
            }
        }

        const response = await currentWindow.fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            keepalive: true,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body,
        });

        return response.ok;
    } catch (error) {
        return false;
    }
};

export const submitReviewFeedback = async (payload = {}) => {
    const currentWindow = safeWindow();

    if (!currentWindow?.fetch) {
        throw new Error('Feedback could not be saved.');
    }

    const endpoint = getUFOVConfig().routes?.reviewFeedback || '/api/reviews/submit';
    const response = await currentWindow.fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            ...payload,
            installation_id: payload.installation_id || getOrCreateOnboardingAnalyticsId(),
            platform: payload.platform || (isNativeUFOVShell() ? 'native' : 'web'),
        }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(data?.message || 'Feedback could not be saved.');
    }

    return data;
};

const createFallbackAnonymousId = () => {
    const randomChunk = Math.random().toString(36).slice(2, 10);
    const brandKey = getUFOVBrandConfig().key || 'ufov';
    return `$RCAnonymousID:${brandKey}-${Date.now().toString(36)}-${randomChunk}`;
};

export const ensureAnonymousAppUserId = async () => {
    const existing = getStoredAppUserId();

    if (existing) {
        return existing;
    }

    if (!sharedAnonymousAppUserIdPromise) {
        sharedAnonymousAppUserIdPromise = (async () => {
            let appUserId = getStoredAppUserId();

            if (appUserId) {
                return appUserId;
            }

            try {
                const { Purchases } = await import('@revenuecat/purchases-js');

                // Another caller can persist an identity while the SDK chunk loads.
                appUserId = getStoredAppUserId();
                if (!appUserId && typeof Purchases.generateRevenueCatAnonymousAppUserId === 'function') {
                    appUserId = Purchases.generateRevenueCatAnonymousAppUserId();
                }
            } catch (error) {
                appUserId = getStoredAppUserId();
            }

            if (!appUserId) {
                appUserId = createFallbackAnonymousId();
            }

            setStoredAppUserId(appUserId);
            return appUserId;
        })().finally(() => {
            sharedAnonymousAppUserIdPromise = null;
        });
    }

    return sharedAnonymousAppUserIdPromise;
};

const getOnboardingAnalyticsConfig = () => getUFOVConfig().analytics?.onboarding || {};

const getOnboardingAnalyticsVersion = () => {
    const version = `${getOnboardingAnalyticsConfig().version || 'v1'}`.trim();

    return version || 'v1';
};

const getOnboardingAnalyticsSentStorageKey = (version = getOnboardingAnalyticsVersion()) => (
    `${UFOV_ONBOARDING_ANALYTICS_SENT_STORAGE_KEY}.${version}`
);

const getOnboardingAnalyticsDeliveredStorageKey = (version = getOnboardingAnalyticsVersion()) => (
    `${UFOV_ONBOARDING_ANALYTICS_DELIVERED_STORAGE_KEY}.${version}`
);

export const getOrCreateOnboardingAnalyticsId = () => {
    const existing = readStorageValue(UFOV_ONBOARDING_ANALYTICS_ID_STORAGE_KEY);

    if (existing) {
        return existing;
    }

    const nextId = createTrackingEventId();
    writeStorageValue(UFOV_ONBOARDING_ANALYTICS_ID_STORAGE_KEY, nextId);
    return nextId;
};

const getSentOnboardingAnalyticsSteps = (version = getOnboardingAnalyticsVersion()) => {
    const sent = readStorageJson(getOnboardingAnalyticsSentStorageKey(version));

    return sent && typeof sent === 'object' && !Array.isArray(sent) ? sent : {};
};

const markOnboardingAnalyticsStepSent = (step, version = getOnboardingAnalyticsVersion()) => {
    writeStorageJson(getOnboardingAnalyticsSentStorageKey(version), {
        ...getSentOnboardingAnalyticsSteps(version),
        [step]: new Date().toISOString(),
    });
};

const getDeliveredOnboardingAnalyticsUpdates = (version = getOnboardingAnalyticsVersion()) => {
    const delivered = readStorageJson(getOnboardingAnalyticsDeliveredStorageKey(version));

    return delivered && typeof delivered === 'object' && !Array.isArray(delivered) ? delivered : {};
};

const normalizeSignatureValue = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => normalizeSignatureValue(item));
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((normalized, key) => {
            if (value[key] !== undefined && typeof value[key] !== 'function') {
                normalized[key] = normalizeSignatureValue(value[key]);
            }

            return normalized;
        }, {});
    }

    return value;
};

const createOnboardingAnalyticsPayloadSignature = (payload = {}) => {
    const signaturePayload = { ...payload };
    delete signaturePayload.event_id;

    return JSON.stringify(normalizeSignatureValue(signaturePayload));
};

const getOnboardingAnalyticsDeliveryKey = (step, dedupe) => (
    `${dedupe ? 'once' : 'update'}:${step}`
);

const getDeliveredOnboardingAnalyticsSignature = (deliveryKey, version = getOnboardingAnalyticsVersion()) => {
    const delivered = getDeliveredOnboardingAnalyticsUpdates(version)[deliveryKey];

    return typeof delivered === 'string' ? delivered : delivered?.signature || '';
};

const markOnboardingAnalyticsUpdateDelivered = (entry) => {
    const version = entry.requestPayload.version || getOnboardingAnalyticsVersion();
    const delivered = getDeliveredOnboardingAnalyticsUpdates(version);

    writeStorageJson(getOnboardingAnalyticsDeliveredStorageKey(version), {
        ...delivered,
        [entry.deliveryKey]: {
            deliveredAt: new Date().toISOString(),
            signature: entry.signature,
        },
    });
};

const normalizeOnboardingAnalyticsOutbox = (value, now = Date.now()) => {
    const entries = Array.isArray(value) ? value : [];
    const oldestAllowed = now - ONBOARDING_ANALYTICS_OUTBOX_RETENTION_MS;

    return entries.filter((entry) => (
        entry
        && typeof entry === 'object'
        && typeof entry.id === 'string'
        && typeof entry.queueKey === 'string'
        && typeof entry.deliveryKey === 'string'
        && entry.requestPayload
        && typeof entry.requestPayload === 'object'
        && typeof entry.requestPayload.step === 'string'
        && Number(entry.createdAt) >= oldestAllowed
    )).map((entry) => ({
        ...entry,
        attempts: Math.max(0, Number(entry.attempts) || 0),
        createdAt: Number(entry.createdAt) || now,
        dedupe: entry.dedupe === true,
        nextAttemptAt: Math.max(0, Number(entry.nextAttemptAt) || 0),
        revision: Math.max(1, Number(entry.revision) || 1),
        updatedAt: Number(entry.updatedAt) || Number(entry.createdAt) || now,
    })).sort((left, right) => left.createdAt - right.createdAt)
        .slice(-ONBOARDING_ANALYTICS_OUTBOX_LIMIT);
};

const getOnboardingAnalyticsOutbox = () => normalizeOnboardingAnalyticsOutbox(
    readStorageJson(UFOV_ONBOARDING_ANALYTICS_OUTBOX_STORAGE_KEY)
);

const writeOnboardingAnalyticsOutbox = (entries) => {
    const normalized = normalizeOnboardingAnalyticsOutbox(entries);
    writeStorageJson(UFOV_ONBOARDING_ANALYTICS_OUTBOX_STORAGE_KEY, normalized);

    return normalized;
};

const enqueueOnboardingAnalyticsUpdate = (requestPayload, dedupe) => {
    const now = Date.now();
    const step = `${requestPayload.step || ''}`.trim();
    const queueKey = [
        requestPayload.analytics_id,
        requestPayload.version,
        dedupe ? 'once' : 'update',
        step,
    ].join(':');
    const deliveryKey = getOnboardingAnalyticsDeliveryKey(step, dedupe);
    const outbox = getOnboardingAnalyticsOutbox();
    const existingIndex = outbox.findIndex((entry) => entry.queueKey === queueKey);
    const existing = existingIndex >= 0 ? outbox[existingIndex] : null;
    const mergedPayload = existing && !dedupe
        ? {
            ...existing.requestPayload,
            ...requestPayload,
            event_id: existing.requestPayload.event_id || requestPayload.event_id,
        }
        : requestPayload;
    const signature = createOnboardingAnalyticsPayloadSignature(mergedPayload);

    if (existing && (dedupe || existing.signature === signature)) {
        return existing;
    }

    const entry = {
        attempts: 0,
        createdAt: existing?.createdAt || now,
        dedupe,
        deliveryKey,
        id: existing?.id || createTrackingEventId(),
        nextAttemptAt: 0,
        queueKey,
        requestPayload: mergedPayload,
        revision: (existing?.revision || 0) + 1,
        signature,
        updatedAt: now,
    };

    if (existingIndex >= 0) {
        outbox[existingIndex] = entry;
    } else {
        outbox.push(entry);
    }

    writeOnboardingAnalyticsOutbox(outbox);
    return entry;
};

const isRetryableOnboardingAnalyticsStatus = (status) => (
    !status
    || status === 408
    || status === 425
    || status === 429
    || status >= 500
);

const postOnboardingAnalyticsUpdate = async (requestPayload) => {
    const currentWindow = safeWindow();
    const endpoint = getUFOVConfig().routes?.onboardingAnalytics || '/api/onboarding/analytics';

    if (!currentWindow) {
        return { ok: false, retryable: true };
    }

    if (currentWindow.axios?.post) {
        try {
            const response = await currentWindow.axios.post(endpoint, requestPayload, {
                headers: { Accept: 'application/json' },
            });
            const status = Number(response?.status) || 0;

            return {
                ok: status >= 200 && status < 300,
                retryable: isRetryableOnboardingAnalyticsStatus(status),
            };
        } catch (error) {
            const status = Number(error?.response?.status) || 0;

            return { ok: false, retryable: isRetryableOnboardingAnalyticsStatus(status) };
        }
    }

    if (typeof currentWindow.fetch !== 'function') {
        return { ok: false, retryable: true };
    }

    try {
        const response = await currentWindow.fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            keepalive: true,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestPayload),
        });
        const status = Number(response?.status) || 0;

        return {
            ok: response?.ok === true,
            retryable: isRetryableOnboardingAnalyticsStatus(status),
        };
    } catch (error) {
        return { ok: false, retryable: true };
    }
};

const getOnboardingAnalyticsRetryDelay = (attempts) => Math.min(
    ONBOARDING_ANALYTICS_RETRY_MAX_MS,
    ONBOARDING_ANALYTICS_RETRY_BASE_MS * (2 ** Math.max(0, Math.min(10, attempts - 1)))
);

const clearOnboardingAnalyticsRetryTimer = () => {
    const currentWindow = safeWindow();

    if (onboardingAnalyticsRetryTimer !== null && currentWindow?.clearTimeout) {
        currentWindow.clearTimeout(onboardingAnalyticsRetryTimer);
    }

    onboardingAnalyticsRetryTimer = null;
};

const scheduleOnboardingAnalyticsOutboxFlush = () => {
    const currentWindow = safeWindow();
    const outbox = getOnboardingAnalyticsOutbox();

    clearOnboardingAnalyticsRetryTimer();

    if (!currentWindow?.setTimeout || !outbox.length || currentWindow.navigator?.onLine === false) {
        return;
    }

    const nextAttemptAt = Math.min(...outbox.map((entry) => entry.nextAttemptAt || 0));
    const delay = Math.max(0, Math.min(2147483647, nextAttemptAt - Date.now()));

    onboardingAnalyticsRetryTimer = currentWindow.setTimeout(() => {
        onboardingAnalyticsRetryTimer = null;
        void flushOnboardingAnalyticsOutbox();
    }, delay);
};

export const flushOnboardingAnalyticsOutbox = async ({ force = false } = {}) => {
    const currentWindow = safeWindow();
    const analyticsConfig = getOnboardingAnalyticsConfig();
    const enabled = analyticsConfig.enabled === undefined
        ? true
        : isTruthyConfigValue(analyticsConfig.enabled);

    if (!currentWindow || !enabled || currentWindow.navigator?.onLine === false) {
        return 0;
    }

    if (onboardingAnalyticsOutboxFlushPromise) {
        return onboardingAnalyticsOutboxFlushPromise;
    }

    onboardingAnalyticsOutboxFlushPromise = (async () => {
        let deliveredCount = 0;

        while (true) {
            const outbox = writeOnboardingAnalyticsOutbox(getOnboardingAnalyticsOutbox());
            const entry = outbox[0];

            if (!entry) {
                break;
            }

            if (!force && entry.nextAttemptAt > Date.now()) {
                break;
            }

            const result = await postOnboardingAnalyticsUpdate(entry.requestPayload);
            const latestOutbox = getOnboardingAnalyticsOutbox();
            const latestIndex = latestOutbox.findIndex((item) => item.id === entry.id);
            const latest = latestIndex >= 0 ? latestOutbox[latestIndex] : null;

            if (result.ok) {
                if (entry.dedupe) {
                    markOnboardingAnalyticsStepSent(
                        entry.requestPayload.step,
                        entry.requestPayload.version || getOnboardingAnalyticsVersion()
                    );
                }
                markOnboardingAnalyticsUpdateDelivered(entry);
                deliveredCount += 1;

                if (latest && latest.revision === entry.revision) {
                    latestOutbox.splice(latestIndex, 1);
                } else if (latest) {
                    latest.attempts = 0;
                    latest.nextAttemptAt = 0;
                }

                writeOnboardingAnalyticsOutbox(latestOutbox);
                continue;
            }

            if (latest && latest.revision !== entry.revision) {
                latest.attempts = 0;
                latest.nextAttemptAt = 0;
                writeOnboardingAnalyticsOutbox(latestOutbox);
                continue;
            }

            if (!result.retryable) {
                if (latest) {
                    latestOutbox.splice(latestIndex, 1);
                    writeOnboardingAnalyticsOutbox(latestOutbox);
                }
                continue;
            }

            if (latest) {
                latest.attempts += 1;
                latest.nextAttemptAt = Date.now() + getOnboardingAnalyticsRetryDelay(latest.attempts);
                latest.updatedAt = Date.now();
                writeOnboardingAnalyticsOutbox(latestOutbox);
            }
            break;
        }

        scheduleOnboardingAnalyticsOutboxFlush();
        return deliveredCount;
    })().finally(() => {
        onboardingAnalyticsOutboxFlushPromise = null;
    });

    return onboardingAnalyticsOutboxFlushPromise;
};

const installOnboardingAnalyticsLifecycle = () => {
    const currentWindow = safeWindow();

    if (!currentWindow || onboardingAnalyticsLifecycleInstalled) {
        return;
    }

    onboardingAnalyticsLifecycleInstalled = true;
    currentWindow.addEventListener?.('online', () => {
        void flushOnboardingAnalyticsOutbox({ force: true });
    });
    currentWindow.setTimeout?.(() => {
        void flushOnboardingAnalyticsOutbox();
    }, 0);
};

const sendOnboardingSessionUpdate = async (payload = {}, dedupe = false) => {
    const currentWindow = safeWindow();
    const analyticsConfig = getOnboardingAnalyticsConfig();
    const enabled = analyticsConfig.enabled === undefined
        ? true
        : isTruthyConfigValue(analyticsConfig.enabled);
    const step = `${payload.step || ''}`.trim();

    if (!currentWindow || !enabled || !step) {
        return false;
    }

    installOnboardingAnalyticsLifecycle();

    const version = getOnboardingAnalyticsVersion();
    const sent = getSentOnboardingAnalyticsSteps(version);

    if (dedupe && sent[step]) {
        return false;
    }

    const appUserId = await ensureAnonymousAppUserId().catch(() => getStoredAppUserId());
    const requestPayload = {
        ...payload,
        analytics_id: getOrCreateOnboardingAnalyticsId(),
        app_user_id: appUserId || getStoredAppUserId(),
        brand: payload.brand || getUFOVBrandConfig().key,
        event_id: payload.event_id || createTrackingEventId(),
        path: payload.path || currentWindow.location?.pathname || '/',
        platform: payload.platform || (isNativeUFOVShell() ? 'native' : 'web'),
        source: payload.source || 'onboarding',
        version,
    };
    const deliveryKey = getOnboardingAnalyticsDeliveryKey(step, dedupe);
    const signature = createOnboardingAnalyticsPayloadSignature(requestPayload);

    if (!dedupe && getDeliveredOnboardingAnalyticsSignature(deliveryKey, version) === signature) {
        return false;
    }

    const entry = enqueueOnboardingAnalyticsUpdate(requestPayload, dedupe);
    await flushOnboardingAnalyticsOutbox();

    if (dedupe) {
        return Boolean(getSentOnboardingAnalyticsSteps(version)[step]);
    }

    return getDeliveredOnboardingAnalyticsSignature(deliveryKey, version) === entry.signature;
};

export const updateOnboardingSession = async (payload = {}) => (
    sendOnboardingSessionUpdate(payload, false)
);

export const trackOnboardingSectionOnce = async (payload = {}) => (
    sendOnboardingSessionUpdate(payload, true)
);

installOnboardingAnalyticsLifecycle();

const createAnonymousRankingToken = () => {
    const currentWindow = safeWindow();

    if (typeof currentWindow?.crypto?.randomUUID === 'function') {
        return currentWindow.crypto.randomUUID();
    }

    if (typeof currentWindow?.crypto?.getRandomValues === 'function') {
        const bytes = new Uint8Array(24);
        currentWindow.crypto.getRandomValues(bytes);
        return Array.from(bytes)
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 18)}`;
};

export const getStoredAnonymousRankingIdentity = () => {
    const leaderboardId = readStorageValue(UFOV_ANONYMOUS_RANKING_ID_STORAGE_KEY);
    const resetToken = readStorageValue(UFOV_ANONYMOUS_RANKING_RESET_TOKEN_STORAGE_KEY);

    if (!leaderboardId || !resetToken) {
        return null;
    }

    return {
        leaderboardId,
        resetToken,
    };
};

export const ensureAnonymousRankingIdentity = () => {
    const existing = getStoredAnonymousRankingIdentity();

    if (existing) {
        return existing;
    }

    const identity = {
        leaderboardId: createAnonymousRankingToken(),
        resetToken: createAnonymousRankingToken(),
    };

    writeStorageValue(UFOV_ANONYMOUS_RANKING_ID_STORAGE_KEY, identity.leaderboardId);
    writeStorageValue(UFOV_ANONYMOUS_RANKING_RESET_TOKEN_STORAGE_KEY, identity.resetToken);

    return identity;
};

export const clearAnonymousRankingIdentity = () => {
    removeStorageValue(UFOV_ANONYMOUS_RANKING_ID_STORAGE_KEY);
    removeStorageValue(UFOV_ANONYMOUS_RANKING_RESET_TOKEN_STORAGE_KEY);
};

const postAnonymousRankingJson = async (path, payload) => {
    const response = await fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(data?.message || 'Anonymous ranking could not be updated.');
    }

    return data;
};

export const submitAnonymousRanking = async (metrics, appUserId = '') => {
    const identity = ensureAnonymousRankingIdentity();

    return postAnonymousRankingJson('/api/ranking/anonymous/submit', {
        leaderboard_id: identity.leaderboardId,
        reset_token: identity.resetToken,
        ...(appUserId ? { app_user_id: appUserId } : {}),
        ...metrics,
    });
};

export const lookupAnonymousRanking = async () => {
    const identity = getStoredAnonymousRankingIdentity();

    if (!identity) {
        return { ranking: null };
    }

    return postAnonymousRankingJson('/api/ranking/anonymous/lookup', {
        leaderboard_id: identity.leaderboardId,
    });
};

export const resetAnonymousRanking = async () => {
    const identity = getStoredAnonymousRankingIdentity();

    if (!identity) {
        clearAnonymousRankingIdentity();
        return { reset: true };
    }

    try {
        return await postAnonymousRankingJson('/api/ranking/anonymous/reset', {
            leaderboard_id: identity.leaderboardId,
            reset_token: identity.resetToken,
        });
    } finally {
        clearAnonymousRankingIdentity();
    }
};

const normalizeIdentifierList = (value, defaults = []) => {
    const items = Array.isArray(value)
        ? value
        : typeof value === 'string'
            ? value.split(',')
            : [];

    const normalized = items
        .map((item) => `${item}`.trim())
        .filter(Boolean);

    return normalized.length ? normalized : defaults;
};

const getPremiumOverrideConfig = () => {
    const config = getUFOVConfig();

    return config.premiumOverride || config.premium_override || {};
};

const createPremiumOverrideSnapshot = (snapshot = {}) => {
    const override = getPremiumOverrideConfig();

    if (!isTruthyConfigValue(override.enabled)) {
        return null;
    }

    return {
        isPremium: true,
        entitlements: normalizeIdentifierList(
            override.entitlementIds || override.entitlement_ids,
            ['SOPT Pro', 'premium']
        ),
        source: override.source || 'local-dev',
        lastCheckedAt: snapshot.lastCheckedAt || new Date().toISOString(),
        appUserId: snapshot.appUserId || getStoredAppUserId(),
        managementUrl: snapshot.managementUrl || '',
        error: '',
    };
};

const createReviewerAccessSnapshot = (snapshot = {}) => {
    const expiresAt = readActiveReviewerAccessExpiry();

    if (!expiresAt) {
        return null;
    }

    return {
        ...snapshot,
        isPremium: true,
        entitlements: [...new Set([
            ...(Array.isArray(snapshot.entitlements) ? snapshot.entitlements : []),
            ...getBillingEntitlementIds(),
        ])],
        source: 'reviewer-access',
        lastCheckedAt: snapshot.lastCheckedAt || new Date().toISOString(),
        appUserId: snapshot.appUserId || getStoredAppUserId(),
        managementUrl: '',
        reviewerAccessExpiresAt: new Date(expiresAt).toISOString(),
        error: '',
    };
};

const clearExpiredReviewerSnapshot = (snapshot = {}) => (
    snapshot.source === 'reviewer-access'
        ? {
            ...snapshot,
            isPremium: false,
            entitlements: [],
            source: 'local',
            managementUrl: '',
            reviewerAccessExpiresAt: null,
        }
        : snapshot
);

export const readPremiumSnapshot = () => {
    const snapshot = readStorageJson(UFOV_PREMIUM_STORAGE_KEY) || {};
    const appUserId = snapshot.appUserId || getStoredAppUserId();

    const normalizedSnapshot = {
        isPremium: snapshot.isPremium === true,
        entitlements: Array.isArray(snapshot.entitlements) ? snapshot.entitlements : [],
        source: snapshot.source || 'local',
        lastCheckedAt: snapshot.lastCheckedAt || null,
        appUserId,
        managementUrl: snapshot.managementUrl || '',
        error: snapshot.error || '',
    };

    return createPremiumOverrideSnapshot(normalizedSnapshot)
        || createReviewerAccessSnapshot(normalizedSnapshot)
        || clearExpiredReviewerSnapshot(normalizedSnapshot);
};

export const writePremiumSnapshot = (snapshot) => {
    const nextSnapshot = {
        ...readPremiumSnapshot(),
        ...snapshot,
        isPremium: snapshot.isPremium === true,
        entitlements: Array.isArray(snapshot.entitlements)
            ? snapshot.entitlements
            : readPremiumSnapshot().entitlements,
        lastCheckedAt: snapshot.lastCheckedAt || new Date().toISOString(),
        managementUrl: typeof snapshot.managementUrl === 'string'
            ? snapshot.managementUrl
            : readPremiumSnapshot().managementUrl,
    };

    const writableSnapshot = createPremiumOverrideSnapshot(nextSnapshot)
        || createReviewerAccessSnapshot(nextSnapshot)
        || clearExpiredReviewerSnapshot(nextSnapshot);

    if (writableSnapshot.appUserId) {
        setStoredAppUserId(writableSnapshot.appUserId);
    }

    writeStorageJson(UFOV_PREMIUM_STORAGE_KEY, writableSnapshot);
    return writableSnapshot;
};

export const clearPremiumSnapshot = ({ clearAppUserId = true } = {}) => {
    removeStorageValue(UFOV_PREMIUM_STORAGE_KEY);

    if (clearAppUserId) {
        removeStorageValue(UFOV_APP_USER_ID_STORAGE_KEY);
    }

    return readPremiumSnapshot();
};

export const getBillingConfig = () => getUFOVConfig().billing || {};

export const getUFOVPaywallConfig = () => getUFOVConfig().paywall || {};

export const getLocalizedConfigValue = (value, language = 'en', fallback = '') => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const exact = value[language];
        const english = value.en;
        const first = Object.values(value).find((item) => typeof item === 'string' && item.trim());

        return `${exact || english || first || fallback || ''}`.trim();
    }

    return `${value || fallback || ''}`.trim();
};

const isConfigEnabled = (value, fallback = true) => {
    if (value === undefined || value === null) {
        return fallback;
    }

    if (typeof value === 'boolean') {
        return value;
    }

    return isTruthyConfigValue(value);
};

const getPaywallPlanOrder = (paywall, rawPlans) => {
    const configuredOrder = normalizeIdentifierList(paywall.planOrder || paywall.plan_order, []);

    return configuredOrder.length
        ? configuredOrder
        : Object.keys(rawPlans).length
            ? Object.keys(rawPlans)
            : ['weekly', 'lifetime'];
};

export const getPaywallScreenConfig = (language = 'en') => {
    const paywall = getUFOVPaywallConfig();

    return {
        activeMode: `${paywall.activeMode || paywall.active_mode || ''}`.trim(),
        selectedPlan: `${paywall.selectedPlan || paywall.selected_plan || ''}`.trim(),
        showAfterOnboarding: isConfigEnabled(paywall.showAfterOnboarding ?? paywall.show_after_onboarding, false),
        allowCloseAfterOnboarding: isConfigEnabled(paywall.allowCloseAfterOnboarding ?? paywall.allow_close_after_onboarding, true),
        title: getLocalizedConfigValue(paywall.title, language, ''),
        description: getLocalizedConfigValue(paywall.description, language, ''),
        onboardingTitle: getLocalizedConfigValue(paywall.onboardingTitle || paywall.onboarding_title, language, ''),
        onboardingDescription: getLocalizedConfigValue(paywall.onboardingDescription || paywall.onboarding_description, language, ''),
    };
};

export const getConfiguredPaywallPlans = (language = 'en', fallbackPlans = {}) => {
    const paywall = getUFOVPaywallConfig();
    const rawPlans = paywall.plans && typeof paywall.plans === 'object' ? paywall.plans : {};
    const planOrder = getPaywallPlanOrder(paywall, rawPlans);

    return planOrder
        .map((planId) => {
            const rawPlan = rawPlans[planId] || {};
            const fallback = fallbackPlans[planId] || {};
            const packageIdentifiers = normalizeIdentifierList(
                rawPlan.packageIdentifiers || rawPlan.package_aliases || rawPlan.packageId || rawPlan.package_id,
                []
            );
            const nativePackageIdentifiers = normalizeIdentifierList(
                rawPlan.nativePackageIdentifiers || rawPlan.native_package_aliases || rawPlan.nativePackageId || rawPlan.native_package_id,
                []
            );
            const priceLine = getLocalizedConfigValue(
                rawPlan.priceLine || rawPlan.price_line || rawPlan.priceFallback || rawPlan.price_fallback,
                language,
                fallback.priceLine || fallback.priceFallback || ''
            );

            return {
                ...fallback,
                id: `${rawPlan.id || planId}`.trim(),
                planKind: `${rawPlan.planKind || rawPlan.plan_kind || fallback.planKind || planId}`.trim(),
                enabled: isConfigEnabled(rawPlan.enabled, true),
                packageIdentifiers,
                packageId: packageIdentifiers.join(','),
                packageIdentifier: packageIdentifiers[0] || '',
                nativePackageIdentifiers,
                nativePackageId: nativePackageIdentifiers.join(','),
                nativePackageIdentifier: nativePackageIdentifiers[0] || '',
                label: getLocalizedConfigValue(rawPlan.label, language, fallback.label || ''),
                priceLine,
                priceFallback: priceLine || fallback.priceFallback || '',
                note: getLocalizedConfigValue(rawPlan.note, language, fallback.note || ''),
                badge: getLocalizedConfigValue(rawPlan.badge, language, fallback.badge || ''),
                ctaLabel: getLocalizedConfigValue(rawPlan.ctaLabel || rawPlan.cta_label, language, fallback.ctaLabel || ''),
                footnote: getLocalizedConfigValue(rawPlan.footnote, language, fallback.footnote || ''),
            };
        })
        .filter((plan) => plan.enabled);
};

export const getConfiguredPaywallSelectedPlan = (plans = [], fallback = 'weekly') => {
    const configuredPlan = `${getUFOVPaywallConfig().selectedPlan || getUFOVPaywallConfig().selected_plan || ''}`.trim();

    if (configuredPlan && plans.some((plan) => plan.id === configuredPlan)) {
        return configuredPlan;
    }

    return plans[0]?.id || fallback;
};

export const getBillingEntitlementIds = () => {
    const billing = getBillingConfig();

    return normalizeIdentifierList(
        billing.entitlementIds || billing.entitlementId,
        ['UFOV Pro', 'U-FOV Pro', 'SOPT Pro', 'premium']
    );
};

export const getBillingOfferingId = () => {
    const billing = getBillingConfig();

    return typeof billing.offeringId === 'string' && billing.offeringId.trim()
        ? billing.offeringId.trim()
        : '';
};

export const getBillingPackageAliases = (plan) => {
    const billing = getBillingConfig();
    const defaults = plan === 'lifetime'
        ? ['$rc_lifetime', 'lifetime']
        : ['$rc_weekly', 'weekly'];

    return normalizeIdentifierList(billing?.packages?.[plan], defaults);
};

const uniqueIdentifiers = (items = []) => {
    const identifiers = items
        .map((item) => `${item || ''}`.trim())
        .filter(Boolean);

    return [...new Set(identifiers)];
};

const getPackageIdentifiers = (targetPackage) => {
    if (!targetPackage) {
        return [];
    }

    return uniqueIdentifiers([
        targetPackage.id,
        targetPackage.identifier,
        targetPackage.packageId,
        targetPackage.packageIdentifier,
        ...(Array.isArray(targetPackage.packageIdentifiers) ? targetPackage.packageIdentifiers : []),
        targetPackage.productIdentifier,
        targetPackage.webBillingProduct?.identifier,
        targetPackage.webBillingProduct?.productIdentifier,
        targetPackage.webBillingProduct?.storeProductIdentifier,
        targetPackage.rcBillingProduct?.identifier,
        targetPackage.rcBillingProduct?.productIdentifier,
        targetPackage.product?.identifier,
        targetPackage.storeProduct?.identifier,
    ]);
};

const getNativePackageIdentifiers = (targetPackage) => {
    if (!targetPackage) {
        return [];
    }

    return normalizeIdentifierList(
        targetPackage.nativePackageIdentifiers
            || targetPackage.native_package_aliases
            || targetPackage.nativePackageId
            || targetPackage.native_package_id,
        []
    );
};

export const normalizeNativePackageOptions = (payload = {}) => {
    const rawPackages = Array.isArray(payload.availablePackages)
        ? payload.availablePackages
        : Array.isArray(payload.packageOptions)
            ? payload.packageOptions
            : Array.isArray(payload.nativePackages)
                ? payload.nativePackages
                : [];

    return rawPackages
        .map((targetPackage) => {
            const identifier = `${targetPackage?.identifier || targetPackage?.packageId || ''}`.trim();
            const productIdentifier = `${targetPackage?.productIdentifier || targetPackage?.productId || ''}`.trim();

            if (!identifier && !productIdentifier) {
                return null;
            }

            return {
                ...targetPackage,
                id: identifier || productIdentifier,
                identifier: identifier || productIdentifier,
                productIdentifier,
                packageType: `${targetPackage?.packageType || ''}`.trim(),
                productTitle: `${targetPackage?.productTitle || targetPackage?.title || ''}`.trim(),
                productDescription: `${targetPackage?.productDescription || targetPackage?.description || ''}`.trim(),
                productCategory: `${targetPackage?.productCategory || ''}`.trim(),
                productType: `${targetPackage?.productType || ''}`.trim(),
                subscriptionPeriod: `${targetPackage?.subscriptionPeriod || ''}`.trim(),
                formattedPrice: `${targetPackage?.formattedPrice || targetPackage?.priceString || ''}`.trim(),
                currencyCode: `${targetPackage?.currencyCode || ''}`.trim(),
            };
        })
        .filter(Boolean);
};

export const getNativePackagePlanKind = (targetPackage = {}) => {
    if (targetPackage.planKind === 'lifetime' || targetPackage.plan_kind === 'lifetime') {
        return 'lifetime';
    }

    if (targetPackage.planKind === 'weekly' || targetPackage.plan_kind === 'weekly') {
        return 'weekly';
    }

    const searchable = [
        targetPackage.identifier,
        targetPackage.productIdentifier,
        targetPackage.productTitle,
        targetPackage.packageType,
        targetPackage.productType,
        targetPackage.productCategory,
        targetPackage.subscriptionPeriod,
    ].join(' ').toLowerCase();

    if (
        targetPackage.packageType === 'LIFETIME'
        || targetPackage.productCategory === 'NON_SUBSCRIPTION'
        || targetPackage.productType === 'NON_CONSUMABLE'
        || searchable.includes('lifetime')
    ) {
        return 'lifetime';
    }

    if (
        targetPackage.packageType === 'WEEKLY'
        || targetPackage.subscriptionPeriod === 'P1W'
        || searchable.includes('weekly')
    ) {
        return 'weekly';
    }

    return 'premium';
};

export const buildNativePurchasePayload = (plan, targetPackage = null, payload = {}) => {
    const nativePackageIdentifiers = getNativePackageIdentifiers(targetPackage);
    const explicitPackageIdentifiers = nativePackageIdentifiers.length
        ? nativePackageIdentifiers
        : getPackageIdentifiers(targetPackage);
    const packageIdentifiers = explicitPackageIdentifiers.length
        ? explicitPackageIdentifiers
        : getBillingPackageAliases(plan);

    return {
        plan,
        ...payload,
        ...(packageIdentifiers.length
            ? {
                packageId: packageIdentifiers.join(','),
                packageIdentifier: packageIdentifiers[0],
                packageIdentifiers,
            }
            : {}),
    };
};

export const matchesPackageAliases = (targetPackage, aliases = []) => {
    if (!targetPackage) {
        return false;
    }

    const knownIdentifiers = getPackageIdentifiers(targetPackage);

    return aliases.some((alias) => knownIdentifiers.includes(alias));
};

export const resolvePackageByAliases = (offering, aliases = []) => {
    if (!offering?.availablePackages?.length) {
        return null;
    }

    for (const alias of aliases) {
        const matchedPackage = offering.availablePackages.find((item) => matchesPackageAliases(item, [alias]));

        if (matchedPackage) {
            return matchedPackage;
        }
    }

    return null;
};

let sharedPurchasesPromise = null;

export const ensureWebRevenueCat = async () => {
    const billing = getBillingConfig();

    if (!billing.publicApiKey || isNativeUFOVShell()) {
        return null;
    }

    if (!sharedPurchasesPromise) {
        sharedPurchasesPromise = (async () => {
            const { Purchases } = await import('@revenuecat/purchases-js');
            const appUserId = await ensureAnonymousAppUserId();

            try {
                Purchases.getSharedInstance();
            } catch (error) {
                Purchases.configure({
                    apiKey: billing.publicApiKey,
                    appUserId,
                });
            }

            return {
                Purchases,
                purchases: Purchases.getSharedInstance(),
                appUserId,
            };
        })().catch((error) => {
            sharedPurchasesPromise = null;
            throw error;
        });
    }

    return sharedPurchasesPromise;
};

export const refreshWebPremiumState = async ({ force = false, appUserId = '' } = {}) => {
    if (appUserId) {
        setStoredAppUserId(appUserId);
    }

    const overrideSnapshot = createPremiumOverrideSnapshot({ appUserId })
        || createReviewerAccessSnapshot({ appUserId });

    if (overrideSnapshot) {
        return writePremiumSnapshot(overrideSnapshot);
    }

    const billing = getBillingConfig();

    if (!billing.publicApiKey) {
        return readPremiumSnapshot();
    }

    const runtime = await ensureWebRevenueCat();

    if (!runtime) {
        return readPremiumSnapshot();
    }

    if (force && typeof runtime.purchases.invalidateCustomerInfoCache === 'function') {
        runtime.purchases.invalidateCustomerInfoCache();
    }

    const customerInfo = await runtime.purchases.getCustomerInfo();
    const activeEntitlements = Object.keys(customerInfo?.entitlements?.active || {});
    const entitlementIds = getBillingEntitlementIds();

    return writePremiumSnapshot({
        isPremium: activeEntitlements.some((identifier) => entitlementIds.includes(identifier)),
        entitlements: activeEntitlements,
        managementUrl: customerInfo?.managementURL || '',
        source: 'web-sdk',
        appUserId: appUserId || runtime.appUserId,
        error: '',
    });
};

export const syncPremiumSnapshotFromNative = (payload = {}) => (
    writePremiumSnapshot({
        isPremium: payload.premium === true,
        entitlements: Array.isArray(payload.entitlements) ? payload.entitlements : [],
        appUserId: typeof payload.appUserId === 'string' ? payload.appUserId : getStoredAppUserId(),
        managementUrl: typeof payload.managementUrl === 'string' ? payload.managementUrl : '',
        source: 'native',
        error: payload.reason || '',
    })
);

export const loadWebBillingContext = async ({ force = false, appUserId = '' } = {}) => {
    const billing = getBillingConfig();

    if (!billing.publicApiKey) {
        return {
            runtime: null,
            customerInfo: null,
            offering: null,
            packages: {
                weekly: null,
                lifetime: null,
            },
            snapshot: readPremiumSnapshot(),
        };
    }

    if (appUserId) {
        setStoredAppUserId(appUserId);
    }

    const runtime = await ensureWebRevenueCat();

    if (!runtime) {
        return {
            runtime: null,
            customerInfo: null,
            offering: null,
            packages: {
                weekly: null,
                lifetime: null,
            },
            snapshot: readPremiumSnapshot(),
        };
    }

    if (force && typeof runtime.purchases.invalidateCustomerInfoCache === 'function') {
        runtime.purchases.invalidateCustomerInfoCache();
    }

    const offeringId = getBillingOfferingId();
    const [customerInfo, offerings] = await Promise.all([
        runtime.purchases.getCustomerInfo(),
        offeringId
            ? runtime.purchases.getOfferings({ offeringIdentifier: offeringId })
            : runtime.purchases.getOfferings(),
    ]);

    const offering = offeringId
        ? offerings.all?.[offeringId] || null
        : offerings.current || null;

    if (offeringId && !offering) {
        throw new Error(`RevenueCat offering "${offeringId}" is not available on this build.`);
    }
    const activeEntitlements = Object.keys(customerInfo?.entitlements?.active || {});
    const entitlementIds = getBillingEntitlementIds();
    const snapshot = writePremiumSnapshot({
        isPremium: activeEntitlements.some((identifier) => entitlementIds.includes(identifier)),
        entitlements: activeEntitlements,
        managementUrl: customerInfo?.managementURL || '',
        source: 'web-sdk',
        appUserId: appUserId || runtime.appUserId,
        error: '',
    });

    return {
        runtime,
        customerInfo,
        offering,
        packages: {
            weekly: resolvePackageByAliases(offering, getBillingPackageAliases('weekly')),
            lifetime: resolvePackageByAliases(offering, getBillingPackageAliases('lifetime')),
        },
        snapshot,
    };
};

export const buildPremiumPageUrl = async ({ reason = 'premium', mode = 'classic' } = {}) => {
    const currentWindow = safeWindow();
    const routes = getUFOVConfig().routes || {};
    const brand = getUFOVBrandConfig();
    const premiumUrl = routes.premium || '/premium';
    const destination = new URL(premiumUrl, currentWindow?.location?.origin || brand.url || 'https://ufov.app');
    const appUserId = await ensureAnonymousAppUserId();

    destination.searchParams.set('reason', reason);
    destination.searchParams.set('mode', mode);
    destination.searchParams.set('app_user_id', appUserId);

    return destination.toString();
};

export const buildPremiumSuccessUrl = ({ redeemUrl = '', appUserId = '' } = {}) => {
    const currentWindow = safeWindow();
    const routes = getUFOVConfig().routes || {};
    const brand = getUFOVBrandConfig();
    const successUrl = routes.premiumSuccess || '/premium/success';
    const destination = new URL(successUrl, currentWindow?.location?.origin || brand.url || 'https://ufov.app');

    if (redeemUrl) {
        destination.searchParams.set('redeem_url', redeemUrl);
    }

    if (appUserId) {
        destination.searchParams.set('app_user_id', appUserId);
    }

    return destination.toString();
};

export const buildWebPurchaseLink = async ({ reason = 'premium', mode = 'classic', packageId = '', offeringId = '' } = {}) => {
    const billing = getBillingConfig();

    if (!billing.purchaseLink) {
        return '';
    }

    const appUserId = await ensureAnonymousAppUserId();
    const purchaseUrl = new URL(billing.purchaseLink);
    const normalizedPath = purchaseUrl.pathname.endsWith('/')
        ? purchaseUrl.pathname
        : `${purchaseUrl.pathname}/`;

    purchaseUrl.pathname = `${normalizedPath}${encodeURIComponent(appUserId)}`;
    purchaseUrl.searchParams.set('skip_purchase_success', 'true');

    const requestedPackageId = `${packageId || billing.packageId || ''}`.trim();

    if (requestedPackageId) {
        purchaseUrl.searchParams.set('package_id', requestedPackageId);
    }

    const requestedOfferingId = `${offeringId || billing.offeringId || ''}`.trim();

    if (requestedOfferingId) {
        purchaseUrl.searchParams.set('offering_id', requestedOfferingId);
    }

    purchaseUrl.searchParams.set('source', getUFOVBrandConfig().key || 'ufov');
    purchaseUrl.searchParams.set('reason', reason);
    purchaseUrl.searchParams.set('mode', mode);

    return purchaseUrl.toString();
};

export const parseBridgePayload = (event) => {
    if (!event) {
        return null;
    }

    if (event?.detail && typeof event.detail === 'object') {
        return event.detail;
    }

    const rawData = event?.data ?? event?.nativeEvent?.data;

    if (!rawData) {
        return null;
    }

    if (typeof rawData === 'object') {
        return rawData;
    }

    try {
        return JSON.parse(rawData);
    } catch (error) {
        return null;
    }
};
